import { compact, groupBy } from 'lodash'
import { ComponentProps, useMemo, useState } from 'react'

import { useSearchableClients } from '../../hooks/useSearchableClients'
import { Client } from '../../models/Client'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { useSubaccountParentUsersByID } from '../../queries/useSubaccountParentUsersByID'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import { ContactAvatar } from './ContactAvatar'
import { NewSubaccountPage } from './NewSubaccountPage'
import { NewUserPage } from './NewUserPage'
import { ComboBox, ComboBoxItem } from '../common/fields/ComboBox'
import { SearchableSelect } from '../common/fields/SearchableSelect'

type Props = {
    mode: 'local' | 'server'
    clients?: User[]
    shouldIncludeSubaccounts?: boolean
    selectedClient?: User | undefined
    selectedSubaccount?: Subaccount | undefined
    onSelectClient?: (client: User | undefined) => void
    onSelectSubaccount?: (subaccount: Subaccount | undefined) => void
    size?: ComponentProps<typeof SearchableSelect>['size']
    shouldPreserveLastSelection?: boolean
    canAddNew?: boolean
    disabled?: boolean
}

type SearchableItem = ComboBoxItem & { searchableText?: string } & (
        | { type: 'none' }
        | { type: 'new-client' }
        | { type: 'new-subaccount' }
        | { type: 'client'; client: User }
        | { type: 'subaccount'; subaccount: Subaccount }
    )

export const ClientSearchableSelect = ({
    mode,
    size,
    selectedClient,
    selectedSubaccount,
    onSelectClient,
    onSelectSubaccount,
    shouldPreserveLastSelection,
    canAddNew,
    disabled,
    shouldIncludeSubaccounts,
    ...props
}: Props) => {
    if (mode === 'local' && props.clients === undefined) {
        console.error(
            "Error: if ClientSearchableSelect.mode is 'local' then you must pass in a 'clients' array"
        )
    }

    const owner = PSApplication.owner()
    const me = PSApplication.me()

    const [searchValue, setSearchValue] = useState('')
    const [isAddNewClientFormOpen, setIsAddNewClientFormOpen] = useState(false)
    const [isAddNewSubaccountFormOpen, setIsAddNewSubaccountFormOpen] = useState(false)

    const {
        clients: serverClients,
        subaccounts: serverSubaccounts,
        isLoading,
    } = useSearchableClients({
        searchValue,
        isEnabled: mode === 'server',
        type: shouldIncludeSubaccounts ? 'client+subaccount' : 'user',
    })

    const clients = mode === 'server' ? serverClients : (props.clients ?? [])
    const subaccounts = mode === 'server' ? serverSubaccounts : []

    const hasSearchValue = searchValue !== ''

    const effectiveSubaccounts = useMemo(() => {
        return selectedSubaccount !== undefined ? [...subaccounts, selectedSubaccount] : subaccounts
    }, [subaccounts, selectedSubaccount])

    const { data: subaccountParentUsersByID } = useSubaccountParentUsersByID(
        effectiveSubaccounts,
        hasSearchValue || selectedSubaccount !== undefined
    )

    const clientSearchableItems = ((): SearchableItem[] => {
        const subaccountItems = shouldIncludeSubaccounts
            ? subaccounts.map(
                  (subaccount): Extract<SearchableItem, { type: 'subaccount' }> => ({
                      type: 'subaccount',
                      id: subaccount.id ?? '',
                      title: subaccount.name ?? '',
                      secondaryTitle: hasSearchValue
                          ? subaccountParentUsersByID?.[subaccount.client?.id ?? '']?.name
                          : undefined,
                      tertiaryView: (
                          <ContactAvatar
                              user={subaccount}
                              className="text-label-md size-9"
                              size="sm"
                          />
                      ),
                      searchableText: subaccount.name ?? '',
                      isNested: !hasSearchValue,
                      subaccount,
                  })
              )
            : []

        const userIDs = new Set(clients.map((client) => client.id))
        const subaccountItemsByUserID = groupBy(
            subaccountItems,
            (subaccountItem) => subaccountItem.subaccount.client?.id ?? ''
        )
        const subaccountItemsWithoutUsers = subaccountItems.filter(
            (subaccountItem) => !userIDs.has(subaccountItem.subaccount.client?.id ?? '')
        )
        const clientAndSubaccountItems: SearchableItem[] = [
            ...clients.flatMap((client) => {
                const clientItem: Extract<SearchableItem, { type: 'client' }> = {
                    type: 'client',
                    id: client.id ?? '',
                    title: client.name ?? '',
                    tertiaryView: <ContactAvatar user={client} className="text-label-md size-9" />,
                    searchableText: `${client.name ?? ''} ${client.e164 ?? ''}`,
                    client,
                }

                return [clientItem, ...(subaccountItemsByUserID[client.id ?? ''] ?? [])]
            }),
            ...(hasSearchValue ? subaccountItemsWithoutUsers : []),
        ]

        const noneItem: SearchableItem = {
            type: 'none',
            id: '__none__',
            title: 'None',
            tertiaryView: <Icon icon="user.small" background="round" className="size-9 shrink-0" />,
        }

        const newClientItem: SearchableItem = {
            type: 'new-client',
            id: '__new-client__',
            title: searchValue === '' ? 'Create new client' : `Create new client: '${searchValue}'`,
            tertiaryView: <Icon icon="plus.small" background="round" className="size-9 shrink-0" />,
        }

        const newSubaccountItem: SearchableItem = {
            type: 'new-subaccount',
            id: '__new-subaccount__',
            title:
                searchValue === ''
                    ? 'Create new sub-account'
                    : `Create new sub-account: '${searchValue}'`,
            tertiaryView: <Icon icon="plus.small" background="round" className="size-9 shrink-0" />,
        }

        return compact([
            (selectedClient !== undefined || selectedSubaccount !== undefined) &&
            (searchValue === '' || searchValue.toLowerCase().includes('none'))
                ? noneItem
                : null,
            ...clientAndSubaccountItems,
            canAddNew ? newClientItem : null,
            canAddNew && shouldIncludeSubaccounts ? newSubaccountItem : null,
        ])
    })()

    const selectedClientItem: SearchableItem | null =
        selectedClient !== undefined
            ? {
                  type: 'client',
                  id: selectedClient.id ?? '',
                  title: selectedClient.name ?? '',
                  tertiaryView: (
                      <ContactAvatar
                          user={selectedClient}
                          className="size-6"
                          size="sm"
                          shouldRestrictToOneLetter
                      />
                  ),
                  searchableText: `${selectedClient.name ?? ''} ${selectedClient.e164 ?? ''}`,
                  client: selectedClient,
              }
            : null
    const selectedSubaccountItem: SearchableItem | null =
        selectedSubaccount !== undefined
            ? {
                  type: 'subaccount',
                  id: selectedSubaccount.id ?? '',
                  title: selectedSubaccount.name ?? '',
                  secondaryTitle:
                      subaccountParentUsersByID?.[selectedSubaccount.client?.id ?? '']?.name,
                  tertiaryView: (
                      <ContactAvatar
                          user={selectedSubaccount}
                          className="text-label-md size-6"
                          size="sm"
                      />
                  ),
                  searchableText: selectedSubaccount.name ?? '',
                  isNested: false,
                  subaccount: selectedSubaccount,
              }
            : null

    return (
        <>
            {mode === 'local' ? (
                <SearchableSelect
                    items={clientSearchableItems}
                    onSelectItem={(item) => {
                        if (item?.type === 'client') {
                            onSelectClient?.(item.client)
                        }
                        else if (item?.type === 'subaccount') {
                            onSelectSubaccount?.(item.subaccount)
                        }
                    }}
                    selectedItemOverride={selectedClientItem}
                    placeholder="Type to search"
                    size={size}
                    shouldPreserveLastSelection={shouldPreserveLastSelection}
                    icon={
                        selectedClientItem?.tertiaryView ?? (
                            <Icon
                                icon="user.small"
                                background="round"
                                className="size-6 shrink-0"
                            />
                        )
                    }
                    disabled={disabled}
                />
            ) : (
                <ComboBox
                    searchValue={searchValue}
                    onSearchValueChange={setSearchValue}
                    onClearSearchValue={() => {
                        setSearchValue('')
                        onSelectClient?.(undefined)
                    }}
                    items={clientSearchableItems}
                    onSelectItem={(item) => {
                        if (item?.type === 'new-client') {
                            setIsAddNewClientFormOpen(true)
                            onSelectClient?.(undefined)
                        }
                        else if (item?.type === 'new-subaccount') {
                            setIsAddNewSubaccountFormOpen(true)
                            onSelectSubaccount?.(undefined)
                        }
                        else if (item?.type === 'client') {
                            setSearchValue(item.client.name ?? '')
                            onSelectClient?.(item.client)
                        }
                        else if (item?.type === 'subaccount') {
                            setSearchValue(item.subaccount.name ?? '')
                            onSelectSubaccount?.(item.subaccount)
                        }
                        else {
                            setSearchValue('')
                            onSelectClient?.(undefined)
                            onSelectSubaccount?.(undefined)
                        }
                    }}
                    selectedItem={selectedSubaccountItem ?? selectedClientItem}
                    placeholder="Type to search"
                    size={size}
                    isLoading={isLoading}
                    shouldPreserveLastSelection={shouldPreserveLastSelection}
                    shouldSelectAllTextOnClick
                    icon={selectedClientItem?.tertiaryView}
                    secondaryButton={
                        canAddNew
                            ? {
                                  label: 'Create new',
                                  onClick: () => setIsAddNewClientFormOpen(true),
                              }
                            : undefined
                    }
                    disabled={disabled}
                />
            )}

            {isAddNewClientFormOpen && (
                <NewUserPage
                    defaultClient={new Client()}
                    defaultUser={new User({ name: searchValue })}
                    me={me}
                    owner={owner}
                    onComplete={(client) => {
                        onSelectClient?.(client)
                        setIsAddNewClientFormOpen(false)
                    }}
                    onClose={() => setIsAddNewClientFormOpen(false)}
                    primaryButtonLabel="Create"
                />
            )}

            {isAddNewSubaccountFormOpen && (
                <NewSubaccountPage
                    defaultSubaccount={
                        new Subaccount({
                            name: searchValue,
                            type: owner.industry?.subaccountType ?? 'none',
                            owner,
                        })
                    }
                    onClose={() => setIsAddNewSubaccountFormOpen(false)}
                    owner={me}
                    onComplete={(subaccount) => {
                        onSelectSubaccount?.(subaccount)
                        setIsAddNewSubaccountFormOpen(false)
                    }}
                />
            )}
        </>
    )
}
